import type { BleDevice, DeviceRole } from './types';

export interface RememberedDevice {
  id: string;
  name: string;
  simulated: boolean;
}

const KEY = 'velostube.devices';

function load(): Partial<Record<DeviceRole, RememberedDevice>> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Record<DeviceRole, RememberedDevice>>;
  } catch {
    return {};
  }
}

export function remember(role: DeviceRole, d: BleDevice): void {
  try {
    const all = load();
    all[role] = { id: d.id, name: d.name ?? '', simulated: !!d.simulated };
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable */
  }
}

export function forget(role: DeviceRole): void {
  try {
    const all = load();
    delete all[role];
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

export function getRemembered(role: DeviceRole): RememberedDevice | null {
  return load()[role] ?? null;
}

/** True if Chrome exposes `navigator.bluetooth.getDevices()` (may need chrome://flags, see HARDWARE_TESTS.md). */
export function canGetDevices(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth && 'getDevices' in navigator.bluetooth;
}

/** Find a previously permitted device object without showing the chooser. */
export async function findPermittedDevice(id: string): Promise<BleDevice | null> {
  if (!canGetDevices()) return null;
  try {
    const devices = await navigator.bluetooth.getDevices();
    return (devices.find((d) => d.id === id) as unknown as BleDevice | undefined) ?? null;
  } catch {
    return null;
  }
}
